import { FC, useCallback, useContext, useMemo, useState } from "react";
import "./BoardMenuColumn.css";
import DragDropGray from "../../../assets/dragDrop.svg";
import DragDropBlue from "../../../assets/dragDropBlue.svg";
import DeleteColumnBlue from "../../../assets/deleteElementGray.svg";
import DeleteColumnGray from "../../../assets/grayDelete.svg";
import EditColumnBlue from "../../../assets/edit.svg";
import EditColumnGray from "../../../assets/editButtonGray.svg";
import ExitCross from "../../../assets/cancelGreey.svg";
import { AppContext } from "../../../App";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { hasPermission } from "../../../helpers/hasPermisson";
import { BoardContext } from "../../../contexts/BoardContext";
import { IColumnDisplay } from "../../../common/api_interfaces/column/IColumnDisplay";
import { hasRestrictedAccessColumn } from "../../../helpers/hasRestrictedAccess";
import { Context } from "../../..";

interface IBoardMenuColumnProps {
    column: IColumnDisplay;
    onColumnDragEnd: () => void;
    draggable: boolean;
    deletable: boolean;
    userAccess: IFunctionPermissions[];
}

const BoardMenuColumn: FC<IBoardMenuColumnProps> = ({ column, onColumnDragEnd, draggable, deletable, userAccess }) => {
    const [renameMode, setRenameMode] = useState<boolean>(false);
    const boardContext = useContext(BoardContext);
    const { showToast } = useContext(AppContext);
    const { store } = useContext(Context);

    const isRestricted = useMemo(
        () =>
            boardContext.accessRestrict &&
            hasRestrictedAccessColumn(boardContext.accessRestrict, column.id, store.user.email),
        [boardContext.accessRestrict, column.id, store.user.email]
    );

    const permissions = useMemo(
        () => ({
            editColumn:
                !isRestricted &&
                (column.proxyBoardId !== undefined
                    ? hasPermission(userAccess, "ColumnProxyAction", "edit")
                    : hasPermission(userAccess, "ColumnAction", "edit")),
            delColumn:
                !isRestricted &&
                (column.proxyBoardId !== undefined
                    ? hasPermission(userAccess, "ColumnProxyAction", "delete")
                    : hasPermission(userAccess, "ColumnAction", "delete")),
            editBoard: hasPermission(userAccess, "BoardAction", "edit"),
        }),
        [userAccess, column, isRestricted]
    );

    const onDragStart = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            const columnElement = (e.target as HTMLElement).closest(".board-menu-column");
            setTimeout(() => {
                columnElement?.classList.add("drag-hidden");
            }, 0);

            e.dataTransfer.setData("application/json", JSON.stringify({ columnId: column.id }));
        },
        [column.id]
    );

    const onDragEnd = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            const columnElement = (e.target as HTMLElement).closest(".board-menu-column");
            setTimeout(() => {
                columnElement?.classList.remove("drag-hidden");
            }, 100);
            onColumnDragEnd();
        },
        [onColumnDragEnd]
    );

    const handleNameChange = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                boardContext.handleEditColumn(column.id, e.currentTarget.value);
                setRenameMode(false);
            }
        },
        [column.id, boardContext.handleEditColumn]
    );

    const toggleRenameMode = useCallback(() => {
        if (!permissions.editColumn) {
            showToast("У вас нет прав для редактирования колонки!");
            return;
        }
        setRenameMode((prev) => !prev);
    }, [permissions.editColumn, showToast]);

    const handleDelete = useCallback(() => {
        if (!permissions.delColumn) {
            showToast("У вас нет прав для удаления колонки!");
            return;
        }
        boardContext.handleDeleteColumn(column.id);
    }, [permissions.delColumn, showToast, boardContext.handleDeleteColumn, column.id]);

    const handleDragClick = useCallback(() => {
        if (!permissions.editBoard) {
            showToast("У вас нет прав для перемещения колонки!");
        }
    }, [permissions.editBoard, showToast]);

    const dragDropIcon = useMemo(() => {
        if (!draggable) return null;
        return permissions.editBoard ? DragDropBlue : DragDropGray;
    }, [draggable, permissions.editBoard]);

    const editIcon = useMemo(
        () => (renameMode ? ExitCross : permissions.editColumn ? EditColumnBlue : EditColumnGray),
        [renameMode, permissions.editColumn]
    );

    const deleteIcon = useMemo(
        () => (permissions.delColumn ? DeleteColumnBlue : DeleteColumnGray),
        [permissions.delColumn]
    );

    return (
        <div id={draggable ? `column-${column.id}` : undefined} className="board-menu-column">
            <div
                className="drag-button"
                draggable={draggable && permissions.editBoard}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
            >
                {draggable && (
                    <div className="dragDrop__edit" onClick={!permissions.editBoard ? handleDragClick : undefined}>
                        {dragDropIcon && <img src={dragDropIcon} alt="Перетащить" />}
                    </div>
                )}
            </div>

            <div className="board-menu-column__block">
                {renameMode ? (
                    <div className="board-menu-column__block--name">
                        <input type="text" defaultValue={column.name} onKeyDown={handleNameChange} autoFocus />
                    </div>
                ) : (
                    <div className="board-menu-column__block--name">{column.name}</div>
                )}

                <div className="board-menu-column__block__button">
                    <button onClick={toggleRenameMode}>
                        <img src={editIcon} alt={renameMode ? "Отменить" : "Редактировать"} />
                    </button>

                    {deletable && (
                        <button onClick={handleDelete}>
                            <img src={deleteIcon} alt="Удалить" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BoardMenuColumn;
